import React from 'react';
import Icon from '../AppIcon';

const ProgressBar = ({ currentStatus, statusHistory, currentLanguage }) => {
  const translations = {
    en: {
      draft: 'Draft',
      offered: 'Offered',
      accepted: 'Accepted',
      in_transit: 'In Transit',
      delivered: 'Delivered',
      completed: 'Completed',
      cancelled: 'Cancelled'
    },
    ko: {
      draft: '초안',
      offered: '제안됨',
      accepted: '수락됨',
      in_transit: '운송 중',
      delivered: '배송됨',
      completed: '완료됨',
      cancelled: '취소됨'
    }
  };

  const t = translations?.[currentLanguage];

  const statuses = [
    { key: 'draft', icon: 'FileText', label: t?.draft },
    { key: 'offered', icon: 'Send', label: t?.offered },
    { key: 'accepted', icon: 'CheckCircle', label: t?.accepted },
    { key: 'in_transit', icon: 'Truck', label: t?.in_transit },
    { key: 'delivered', icon: 'Package', label: t?.delivered },
    { key: 'completed', icon: 'CheckCircle2', label: t?.completed }
  ];

  const currentIndex = statuses?.findIndex(s => s?.key === currentStatus);

  const getStatusColor = (index) => {
    if (currentStatus === 'cancelled') return 'var(--color-error)';
    if (index <= currentIndex) return 'var(--color-success)';
    return 'var(--color-muted)';
  };

  const getStatusBgColor = (index) => {
    if (currentStatus === 'cancelled') return 'bg-error/10';
    if (index <= currentIndex) return 'bg-success/10';
    return 'bg-muted/30';
  };

  if (currentStatus === 'cancelled') {
    return (
      <div className="bg-error/10 rounded-lg p-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-error/20 rounded-full flex items-center justify-center">
          <Icon name="XCircle" size={20} color="var(--color-error)" />
        </div>
        <div>
          <p className="font-semibold text-error">{t?.cancelled}</p>
          <p className="text-sm text-muted-foreground">This order has been cancelled</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-4 md:p-6">
      <div className="relative">
        {/* Progress Line */}
        <div className="absolute top-5 left-0 right-0 h-1 bg-muted hidden md:block">
          <div
            className="h-full bg-success transition-all duration-500"
            style={{ width: `${(currentIndex / (statuses?.length - 1)) * 100}%` }}
          />
        </div>

        {/* Status Steps */}
        <div className="relative flex flex-col md:flex-row justify-between gap-4 md:gap-2">
          {statuses?.map((status, index) => {
            const isActive = index <= currentIndex;
            const isCurrent = index === currentIndex;
            const history = statusHistory?.find(h => h?.status === status?.key);

            return (
              <div key={status?.key} className="flex md:flex-col items-center md:items-center gap-3 md:gap-2 flex-1">
                <div className="flex md:flex-col items-center gap-3 md:gap-2">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      getStatusBgColor(index)
                    } ${isCurrent ? 'ring-4 ring-primary/20' : ''}`}
                  >
                    <Icon
                      name={status?.icon}
                      size={20}
                      color={getStatusColor(index)}
                    />
                  </div>
                  <div className="flex-1 md:text-center">
                    <p
                      className={`text-sm font-medium ${
                        isActive ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {status?.label}
                    </p>
                    {history?.timestamp && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(history?.timestamp)?.toLocaleDateString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgressBar;